import { useEventStore } from '@/stores/eventsStore'
import { useUserStore } from '@/stores/userStore'
import { useEventTypeStore } from '@/stores/eventTypeStore'
import type {
    Address,
    Contact,
    EventDto,
    EventImage,
    EventOrganizer,
    EventOrganizerType,
    EventPackageOrder,
    EventPackageOrderProduct,
    EventType,
    EventTypeDto,
    FieldDtoBase,
    Location,
    SectionDto
} from '@/types'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, reactive, ref, Ref, watch } from 'vue'
import { useEventOrganizerStore } from './eventOrganizer'
import { useEventOrganizerTypeStore } from './eventOrganizerType'
import { isDateField, isTagsField } from '@/utils/fields'

export interface NewEventStore {
    eventType: Ref<EventType>
    eventOrganizer: Ref<EventOrganizer>
    eventOrganizerType: Ref<EventOrganizerType>
    newEvent: EventDto
    setEventInfo: (newEventType: EventType, organizer: EventOrganizer) => void
    saveEvent: () => Promise<void>
}

function createNewEvent() {
    return {
        id: 0,
        typeId: 0,
        created: new Date(),
        updated: new Date(),
        description: undefined,
        title: undefined,
        address: undefined,
        contact: {} as Contact,
        expectedParticipants: undefined,
        hideAddress: false,
        hideContact: false,
        gatedContent: false,
        start: new Date(),
        end: new Date(),
        location: {
            heading: undefined,
            latitude: undefined,
            longitude: undefined,
            useStreetView: false,
            pitch: undefined,
            zoom: undefined
        } as Location,
        image: {
            fileName: '',
            description: '',
            url: '',
            photographer: '',
            altText: '',
            fileExtension: '',
            id: '',
            path: '',
        } as EventImage,
        deleted: undefined,
        locationDirections: undefined,
        suitableFor: undefined,
        private: false,
        signupInfo: '',
        teaser: '',
        tags: [] as number[],
        packageId: undefined,
        packageData: {
            contact: {} as Contact,
            address: {} as Address,
            isBusinessAddress: false,
            products: [] as EventPackageOrderProduct[]
        } as EventPackageOrder
    } as unknown as EventDto
}

export const useNewEventStore = defineStore('NewEventStore', function () {
    const eventTypeStore = useEventTypeStore()
    const eventOrganizerStore = useEventOrganizerStore()
    const eventOrganizerTypeStore = useEventOrganizerTypeStore()
    const eventStore = useEventStore()
    const userStore = useUserStore()
    const newEvent = reactive<EventDto>(createNewEvent())
    const newEventImageFile = ref<File | undefined>(undefined)
    const newEventImageDataUrl = ref<string | undefined>()
    const newEventPackageSelected = computed(() => {
        return newEvent.packageData?.products?.length !== 0 ?? false
    })
    watch(newEventImageFile, async (file) => {
        newEventImageDataUrl.value = file ? await toBase64(file) : undefined
    })
    const organizerId = ref<number | null>(null)
    const organizerPartitionKey = ref<string | null>(null)
    const eventType = computed(() => eventTypeStore.getEventType(newEvent.typeId as number))
    const eventOrganizer = computed(() => eventOrganizerStore.getEventOrganizer(newEvent.creatorId as number))
    const eventOrganizerType = computed(() =>
        eventOrganizerTypeStore.getEventOrganizerType(newEvent.organizerTypeId as number)
    )
    const isNewEvent = ref(true)

    function setEventInfo(newEventType: EventTypeDto, organizer: EventOrganizer) {
        isNewEvent.value = true
        newEventImageFile.value = undefined
        Object.assign(newEvent, createNewEvent())
        newEvent.typeId = newEventType.id as number
        newEvent.creatorId = organizer.id as number
        newEvent.creatorPartitionKey = organizer.partitionKey
        newEvent.organizerTypeId = organizer.typeId
        newEvent.userId = userStore.user?.id
        setDefaultValues(newEvent, newEventType)
    }

    function setDefaultValues(event: EventDto, eventType: EventTypeDto) {
        for (const field of eventType.sections.flatMap((section: SectionDto) => section.fields) as FieldDtoBase[]) {
            if (isDateField(field)) {
                const now = new Date()
                let startDate
                let endDate
                if (field.startDefault) {
                    startDate = new Date(field.startDefault)
                } else {
                    startDate = new Date()
                    startDate.setHours(now.getHours() + 1, 0, 0, 0)
                }
                if (field.endDefault) {
                    endDate = new Date(field.endDefault)
                } else {
                    endDate = new Date()
                    endDate.setTime(startDate.getTime() + 60 * 60 * 1000)
                }

                event.start = startDate
                event.end = endDate
            } else if (isTagsField(field)) {
                if (field.defaults?.length) {
                    event.tags = field.defaults as number[]
                }
            }
        }
    }

    function setEventFromExistingEvent(event: EventDto) {
        isNewEvent.value = false
        Object.assign(newEvent, event)
    }
    function isDefined(product: EventPackageOrderProduct | undefined): product is EventPackageOrderProduct {
        return product !== undefined
    
    }


    async function publishEvent() {
        await eventStore.publishEvent(newEvent);
    }

    async function saveEvent() {
        let data = { ...newEvent }
        const packageData = data.packageData

        if (!newEventPackageSelected.value) {
            data.packageData = {} as EventPackageOrder
        }
        //   console.log(packageData, data.packageData)
        const result = await eventStore.saveNewEvent(data, newEventImageFile.value)
        //   console.log(result)
        data.id = result.id
        data.packageData = packageData
        Object.assign(newEvent, data)
        return result
    }

    return {
        isNewEvent,
        newEvent,
        eventOrganizerType,
        newEventImageFile,
        newEventImageDataUrl,
        newEventPackageSelected,
        eventType,
        organizerPartitionKey,
        organizerId,
        eventOrganizer,
        setEventInfo,
        setEventFromExistingEvent,
        publishEvent,
        saveEvent
    }
})
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useNewEventStore, import.meta.hot))
}
async function toBase64(file?: File): Promise<string | undefined> {
    if (file === undefined) return ''
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = (error) => reject(error)
    })
}
